import { schema } from 'normalizr';
import moment from 'moment-timezone';
import compact from 'lodash/compact';
import assign from 'lodash/assign';
import omit from 'lodash/omit';

import { fromRruleString } from '../helpers/rrule';

const momentWrap = ISODate => moment(ISODate);
const calendarOpts = {
  sameDay: 'HH:mm',
  lastDay: 'MM/DD',
  lastWeek: 'MM/DD',
  sameElse: 'MM/DD',
};

const getFullName = (props) => {
  if (props.name) {
    return props.name;
  } else if (props.firstName && props.lastName) {
    return `${props.firstName} ${props.lastName}`;
  }
  return '';
};

const getInitials = name => compact(name.split(' ').slice(0, 2))
  .map(it => it.charAt(0))
  .join('');

const uuidv1ToDate = (uuid) => {
  const uuidArr = uuid.split('-');
  const timeStr = [
    uuidArr[2].substring(1),
    uuidArr[1],
    uuidArr[0],
  ].join('');
  const intTime = parseInt(timeStr, 16) - 122192928000000000;
  const ms = Math.floor(intTime / 10000);
  return ms;
};

export const userMetadata = new schema.Entity('users', {}, {
  processStrategy(input) {
    const fullName = getFullName(input.attributes);
    return {
      ...input,
      ...input.attributes,
      attributes: undefined,
      fullName,
      initials: getInitials(fullName),
    };
  },
});

const userAssignEntity = (input) => {
  const fullName = getFullName(input);
  return ({
    ...input,
    fullName,
    initials: getInitials(fullName),
    joinedAt: input.joinedAt && uuidv1ToDate(input.joinedAt),
    lastActivityAt: input.lastActivityAt && new Date(input.lastActivityAt).getTime(),
    roles: input.roles || [],
  });
};

export const user = new schema.Entity('users', {}, {
  processStrategy: userAssignEntity,
});

user.define({
  bannedBy: user,
});

export const participant = new schema.Entity('users', { bannedBy: user }, {
  processStrategy: userAssignEntity,
});

participant.define({
  user,
});

export const message = new schema.Entity('messages', { user, editedBy: user }, {
  processStrategy(input) {
    return {
      ...input,
      humanTime: moment(input.createdAt).calendar(null, calendarOpts),
    };
  },
});

export const pin = new schema.Entity('messages', { message, unpinnedBy: user, pinnedBy: user }, {
  idAttribute: 'messageId',
  processStrategy(input) {
    const output = omit(input, 'id');
    output.pinnedAt = new Date(output.pinnedAt).getTime();
    return output;
  },
});

export const pollAnswer = new schema.Entity('pollAnswers', {}, {
  processStrategy(input) {
    return {
      ...input,
      attributes: undefined,
      ...input.attributes,
    };
  },
});

export const poll = new schema.Entity('polls', { answers: new schema.Array({ pollAnswer }, 'type') }, {
  processStrategy(input) {
    return {
      ...input,
      relations: undefined,
      answers: input.relations.answers.data,
    };
  },
});

export const event = new schema.Entity('events', {}, {
  idAttribute: 'id',
  processStrategy(input) {
    const output = omit(input, 'start_time', 'rrule', 'tags');

    output.start = input.start_time && input.start_time.map(momentWrap);
    output.tags = input.tags || [];
    assign(output, fromRruleString(input.rrule), { start: output.start || [] });

    return output;
  },
});

export const room = new schema.Entity('rooms', {}, {
  processStrategy(input) {
    return {
      size: input.size,
    };
  },
});

export const contest = new schema.Entity('contests', { poll }, {
  processStrategy(input) {
    return {
      ...input,
      votesCount: input.votesCount || 0,
      relations: undefined,
      poll: (input.hasQuestions) ? input.relations.poll.data : undefined,
      users: (!input.hasQuestions) ? input.relations.users.data : undefined,
    };
  },
});

// basic map
export const types = new schema.Array({
  message,
  user,
  pin,
  participant,
  event,
  poll,
  pollAnswer,
  room,
  contest,
}, 'type');
